import { RingModel } from "../../Model/RingModel";
import { RenderEngine } from "./core/RenderEngine";
import * as THREE from "three";


/**
 * Handles updates of engraving on the 3D-model.
 */

export class EngravingController {
  private _renderEngine:RenderEngine;
  private _canvasHeight:number; //Height (=> resolution) of the canvas used to create the engraving texture
  private _fontHeightPercentage:number; //Percantage of engraving height the font takes up (=> how much vertical padding it has)
  private _maxEngravingHeight:number; //ringWidth beyond which engraving stops scaling up

  constructor(renderEngine:RenderEngine) {
    this._renderEngine = renderEngine;
    this._canvasHeight = 300;
    this._maxEngravingHeight = 5;
    this._fontHeightPercentage = 0.85;

    this.initialize();
  };// /constructor()

  public initialize = ():Promise<void> => {
    //Make sure all of our fonts are fully loaded before we try to change the engraving
    // return new Promise((resolve, reject) => {
    //   const fonts = [
    //     new FontFaceObserver('Brush Script'),
    //     new FontFaceObserver('Century Gothic'),
    //     new FontFaceObserver('Snell Roundhand'),
    //     new FontFaceObserver('Trajan'),
    //     new FontFaceObserver('Dakota')
    //   ]
    //   const promises = fonts.map(f => f.load());
      
    //   Promise.all(promises).then(() => resolve());
    // });

    //DEBUG: Skip all of the above
    return Promise.resolve();
  };// /initialize()

  public updateEngraving = async (currentRing:RingModel, ringWidth:number, engravingText:string, engravingFont:string) => {
    //console.log("updateEngraving", currentRing, engravingText, engravingFont);

    const { 
      _renderEngine: renderEngine,
      _maxEngravingHeight: maxEngravingHeight,
      _canvasHeight: canvasHeight,
      _fontHeightPercentage: fontHeightPercentage
    } = this;
    const { morphOptions } = currentRing;

    const engravingSizeFactor = (ringWidth > maxEngravingHeight) ? (maxEngravingHeight/ ringWidth) : 1;

    const meshes = await renderEngine.engine.services.render.getAllMeshesOfCurrentScene()

    //Get ring circumference
    if (!currentRing.circumference) {
      const match = meshes.find((m) => m.parent.name === currentRing.SKU)
      if (!match) throw new Error("Could not find mesh grouping for ring. Grouping name needs to match SKU.");

      const ringGroup = match.parent;
      const box = new THREE.Box3().setFromObject(ringGroup); //This gives us a rough approximating of the ring's bounding box but factors in shapeKeys and "wiggle room", so we can only use this as a starting point.

      let maxY = 0;
      let minY = 0;

      //Find lowest point of ring without any morphTargetInfluences, i.e. all of them at 0
      for (const mesh of ringGroup.children) {
        const geometry = (mesh as THREE.Mesh<THREE.BufferGeometry>).geometry;
        const position = geometry.getAttribute('position');

        for (let i = 0; i < position.count; i++) {
          const y = position.getY(i);
          if (y > maxY) maxY = y;
          if (y < minY) minY = y;
        };
      };

      box.max.y = maxY/1000 - morphOptions.thickness[0]/1000;
      box.min.y = minY/1000 + morphOptions.thickness[0]/1000;

      // const boxHelper = new THREE.Box3Helper(box, new THREE.Color(0xff0000) );
      // renderEngine.engine.services.render.scene.add(boxHelper);
      // boxHelper.updateMatrix();

      const ringDiameter = (box.max.y - box.min.y) * 1000;
      const ringCircumference = ringDiameter * Math.PI;

      currentRing.circumference = ringCircumference;
    };
    //--

    const engravingZoneWidth = currentRing.circumference * 0.75; //Engraving zone always takes up roughly 3/4 of the ring

    const engravingGrouping = renderEngine.configurator.config.groupings.find(g => g.name.startsWith(`${currentRing.SKU}_Engraving`));

    try {
      if (!engravingGrouping) return;
      engravingGrouping.object_names.forEach((objName) => {
        const engravingMesh = meshes.find(mesh => mesh.name === objName);
        const engravingMaterial = (engravingMesh.material as THREE.MeshPhysicalMaterial);

        //Get percentage of ring width the engraving zone takes up
        if (!currentRing.ringWidthToEngravingZoneHeightFactor) {
          const box = new THREE.Box3().setFromObject(engravingMesh); //This gives us a rough approximating of the engraving zone's bounding box but factors in shapeKeys and "wiggle room", so we can only use this as a starting point.

          const position = (engravingMesh as THREE.Mesh<THREE.BufferGeometry>).geometry.getAttribute('position');
          let maxX = 0;
          let minX = 0;

          for (let i = 0; i < position.count; i++) {
            const x = position.getX(i);
            if (x > maxX) maxX = x;
            if (x < minX) minX = x;
          };

          box.max.x = maxX/1000;
          box.min.x = minX/1000;

          // const boxHelper = new THREE.Box3Helper(box, new THREE.Color(0xff0000));
          // renderEngine.engine.services.render.scene.add(boxHelper);
          // boxHelper.updateMatrix();

          const minEngravingZoneHeight = (box.max.x - box.min.x) * 1000;
          const minRingWidth = morphOptions.width[0];

          currentRing.ringWidthToEngravingZoneHeightFactor = minEngravingZoneHeight/ minRingWidth;
        };
        //--

        const engravingZoneHeight = ringWidth * currentRing.ringWidthToEngravingZoneHeightFactor;
        const canvasAspectRatio = engravingZoneWidth/ engravingZoneHeight;

        let engravingCanvas = document.querySelector('#engraving-canvas') as HTMLCanvasElement;
        let bumpCanvas = document.querySelector('#bump-canvas') as HTMLCanvasElement;
        if (!engravingCanvas) {
          engravingCanvas = document.createElement('canvas');
        
          // engravingCanvas.id = 'engraving-canvas';
          // engravingCanvas.style.border = '2px solid red';
          // engravingCanvas.style.position = 'fixed';
          // engravingCanvas.style.top = '0px';
          // engravingCanvas.style.left = '0px';
          // engravingCanvas.style.pointerEvents = 'none';
          // document.body.appendChild(engravingCanvas);
        };

        if (!bumpCanvas) {
          bumpCanvas = document.createElement('canvas');
        
          // bumpCanvas.id = 'bump-canvas';
          // bumpCanvas.style.border = '2px solid red';
          // bumpCanvas.style.position = 'fixed';
          // bumpCanvas.style.top = '305px';
          // bumpCanvas.style.left = '0px';
          // document.body.appendChild(bumpCanvas);
        };

        engravingCanvas.height = canvasHeight;
        engravingCanvas.width = engravingCanvas.height * canvasAspectRatio;
        bumpCanvas.height = canvasHeight;
        bumpCanvas.width = bumpCanvas.height * canvasAspectRatio;

        const bumpCanvasContext = bumpCanvas.getContext('2d');
        bumpCanvasContext.fillStyle = 'white';
        bumpCanvasContext.fillRect(0, 0, bumpCanvas.width, bumpCanvas.height);
        bumpCanvasContext.textBaseline = 'middle';
        bumpCanvasContext.textAlign = 'left';

        const engravingCanvasContext = engravingCanvas.getContext('2d');
        // engravingCanvasContext.clearRect(0, 0, engravingCanvas.width, engravingCanvas.height);
        engravingCanvasContext.textBaseline = 'middle';
        engravingCanvasContext.textAlign = 'left';

        //Recursively test if text with a given font size would fit the canvas. Scale it down a little if it doesn't, otherwise return the font size.
        const getFontSize = (testSize:number):number => {
          engravingCanvasContext.font = `${testSize}px ${engravingFont}`;
          bumpCanvasContext.font = `${testSize}px ${engravingFont}`;
          const textWidth = engravingCanvasContext.measureText(engravingText).width;
          
          if (textWidth > engravingCanvas.width*0.98) {
            return getFontSize(testSize*0.98);
          } else {
            return (testSize);
          }
        };// /const getFontSize()

        getFontSize(engravingCanvas.height * fontHeightPercentage * engravingSizeFactor);

        engravingCanvasContext.fillStyle = 'rgba(255, 255, 255, 1)';
        engravingCanvasContext.fillText(engravingText, engravingCanvas.width*0.025, engravingCanvas.height/2);

        bumpCanvasContext.fillStyle = 'rgba(0, 0, 0, 1)';
        bumpCanvasContext.fillText(engravingText, engravingCanvas.width*0.025, engravingCanvas.height/2);

        engravingMaterial.map = new THREE.Texture(engravingCanvas);
        engravingMaterial.map.flipY = false;
        engravingMaterial.map.needsUpdate = true;

        engravingMaterial.bumpMap = new THREE.Texture(bumpCanvas);
        engravingMaterial.bumpMap.flipY = false;
        engravingMaterial.bumpMap.needsUpdate = true;
        
        engravingMaterial.bumpScale = 0.00002;
        engravingMaterial.normalMap = null;

        engravingMaterial.needsUpdate = true;
      });
    } catch(e) {
      console.error('Mesh/ Material not found for engravingGrouping!', engravingGrouping, e);
    };
  };
};
